import { DateTime } from "luxon";
import * as React from "react";
import { connect } from "react-redux";
import { Alert, Button, Modal } from "@mantine/core";
import ABAApi from "../../data/api/abaApi";
import { IState } from "../../data/reducers/state";
import { ITimeSheetEntry } from "../../types/timesheet/Timesheet";
import { toDateTime } from "../../util/date+helper";
import { CenteredLoader } from "../common/loader";
import { SSTable } from "../common/TableData";
import CreateTimesheetEntry from "../timesheet/CreateTimesheetEntry";
import { IProfileTabProps } from "./ProfileTabType";

export interface IPersonalPaymentHistoryViewProps extends IProfileTabProps {
    actions: {
    }
}

const PaymentHistoryView: React.FC<IPersonalPaymentHistoryViewProps> = (props) => {
    const [error, setError] = React.useState<string | null>(null);
    const [isLoaded, setIsLoaded] = React.useState<boolean>(false);
    const [isCreatingEntry, setCreatingEntry] = React.useState<boolean>(false);
    const [entries, setEntries] = React.useState<ITimeSheetEntry[] | undefined>();
    const [selectedPayEvent, setSelectedPayEvent] = React.useState<any>(null);
    const [showDetailsModal, setShowDetailsModal] = React.useState<boolean>(false);

    React.useEffect(() => {
        document.title = `Stadium Supervisor - ${props.person.preferredName || props.person.firstName
            } payment history`;

        ABAApi
            .getPersonPaymentHistory(props.person._id)
            .then(_entries => {
                setIsLoaded(true);
                setEntries(_entries);
            })
            .catch((er) => setError(er.message));
    }, []);

    if (error) return <Alert color="red">{error}</Alert>;

    if (!props.isActive) return <div />;

    if (!isLoaded) {

        return <CenteredLoader />;
    }

    return (
        <div style={{ overflowX: "hidden" }}>
            {/* <Button onClick={() => setCreatingEntry(true)} variant="outline"
                // align right
                style={{ float: "right" }}
                size="sm"
            >
                Add Entry
            </Button> */}

            {(entries || []).length === 0 ? (
                // Empty state
                <div className="text-center py-5">
                    <div className="mb-4">
                        <i className="fas fa-receipt fa-3x text-muted"></i>
                    </div>
                    <h5 className="text-muted">No Payment History</h5>
                    <p className="text-muted">
                        You haven't been included in any pay events yet.<br />
                        This will show your payment history once you start receiving payments for your appointments.
                    </p>
                </div>
            ) : (
                <SSTable>
                    <thead>
                        <tr>
                            <th>Payment Date</th>
                            <th>Amount Paid</th>
                            <th>Appointments</th>
                            <th>Details</th>
                        </tr>
                    </thead>
                    <tbody>
                        {(entries || []).map((payEvent) => {
                            const tooltipId = `payevent-${payEvent.payEventId}-details`;
                            return (
                                <tr key={payEvent.payEventId}>
                                    <td>
                                        {toDateTime(payEvent.paidAt).toLocaleString(DateTime.DATE_MED)}
                                        <br />
                                        <small className="text-muted">
                                            {toDateTime(payEvent.paidAt).toLocaleString(DateTime.TIME_SIMPLE)}
                                        </small>
                                    </td>
                                    <td>
                                        <strong>${payEvent.totalAmountForPerson.toFixed(2)}</strong>
                                    </td>
                                    <td>
                                        <span className="badge badge-info">
                                            {payEvent.appointmentCount} appointment{payEvent.appointmentCount !== 1 ? 's' : ''}
                                        </span>
                                    </td>
                                    <td>
                                        <button
                                            className="btn btn-sm btn-outline-info"
                                            type="button"
                                            onClick={() => {
                                                setSelectedPayEvent(payEvent);
                                                setShowDetailsModal(true);
                                            }}
                                        >
                                            View Details
                                        </button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </SSTable>
            )}
            <Modal 
                opened={!!isCreatingEntry} 
                onClose={() => setCreatingEntry(false)}
                title="Create timesheet entry"
            >
                <div style={{ padding: "xl" }}>
                    <CreateTimesheetEntry
                        onCancel={() => setCreatingEntry(false)}
                        onCreated={() => {
                            window.location.reload();
                        }}
                    />
                </div>
                <div style={{ marginTop: "1rem", display: "flex", justifyContent: "flex-end" }}>
                    <Button
                        color="gray"
                        style={{ margin: "0 10px 0 auto" }}
                        onClick={() => setCreatingEntry(false)}
                    >
                        Close
                    </Button>
                </div>
            </Modal>

            {/* Payment Details Modal */}
            <Modal 
                opened={showDetailsModal} 
                onClose={() => setShowDetailsModal(false)} 
                size="lg"
                title="Payment Details"
            >
                {selectedPayEvent && (
                    <div>
                        <div className="mb-3">
                            <h6>Payment Information</h6>
                            <div className="row">
                                <div className="col-md-6">
                                    <strong>Payment Date:</strong><br />
                                    {toDateTime(selectedPayEvent.paidAt).toLocaleString(DateTime.DATETIME_MED)}
                                </div>
                                <div className="col-md-6">
                                    <strong>Total Amount:</strong><br />
                                    <span className="text-success h5">${selectedPayEvent.totalAmountForPerson.toFixed(2)}</span>
                                </div>
                            </div>
                        </div>

                        <hr />

                        <div>
                            <h6>Appointments Included ({selectedPayEvent.appointmentCount})</h6>
                            <div className="table-responsive">
                                <table className="table table-sm">
                                    <thead>
                                        <tr>
                                            <th>Date</th>
                                            <th>Time</th>
                                            <th>Type</th>
                                            <th>Details</th>
                                            <th className="text-right">Amount</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {selectedPayEvent.appointments.map((apt) => (
                                            <tr key={apt._id}>
                                                <td>{toDateTime(apt.start).toLocaleString(DateTime.DATE_SHORT)}</td>
                                                <td>
                                                    {toDateTime(apt.start).toLocaleString(DateTime.TIME_SIMPLE)} -
                                                    {toDateTime(apt.end).toLocaleString(DateTime.TIME_SIMPLE)}
                                                </td>
                                                <td>
                                                    <span className={`badge ${apt.type === 'game' ? 'badge-primary' : 'badge-success'}`}>
                                                        {apt.type === 'game' ? 'Game' : 'Supervisor'}
                                                    </span>
                                                </td>
                                                <td>
                                                    {apt.type === 'game'
                                                        ? `${apt.details.position || 'referee'} on ${apt.details.court || 'court'}`
                                                        : 'Supervisor duties'
                                                    }
                                                </td>
                                                <td className="text-right">
                                                    <strong>${apt.amount.toFixed(2)}</strong>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                        
                        <div style={{ marginTop: "1rem", display: "flex", justifyContent: "flex-end" }}>
                            <Button color="gray" onClick={() => setShowDetailsModal(false)}>
                                Close
                            </Button>
                        </div>
                    </div>
                )}
            </Modal>
        </div>
    );
}

function mapStateToProps(state: IState, props: IPersonalPaymentHistoryViewProps) {
    return {

    };
}

function mapDispatchToProps(dispatch) {
    return {
        // actions: bindActionCreators({ }, dispatch),
    };
}

export default connect(
    mapStateToProps,
    mapDispatchToProps,
)(PaymentHistoryView);
